import { useCallback, useRef, useState } from 'react';
import axios from 'axios';

import { reportAxiosError } from '~/Utils';
import { downloadResponseAsFile } from '~/Utils/fileDownload';

interface DownloadableResponse {
  data: BlobPart;
  headers: Record<string, string | undefined>;
}

interface DownloadOptions {
  /**
   * Handle the failure yourself instead of the default error dialog.
   *
   * The hook never decides how a screen presents a failed download: pass this and it reports
   * nothing and opens nothing, leaving the whole presentation to you (an inline banner, say).
   * Leave it out and the failure goes to ``reportAxiosError``, which is what a screen with no
   * error surface of its own wants.
   *
   * The error arrives with its Blob body already re-read and parsed, so
   * ``getDownloadErrorMessage`` reads the server's message off it.
   */
  onError?: (error: unknown) => void | Promise<void>;
}

interface UseFileDownloadResult {
  /**
   * Run a request and save its response as a file. Resolves when the download has been handed to
   * the browser, or immediately if a download from this hook instance is already in flight.
   *
   * ``fallbackFilename`` is required rather than defaulted: it is only used when the server sends no
   * ``Content-Disposition``, and in that case a generic default would save every such file under the
   * same meaningless name. Requiring it makes each call site decide.
   */
  download: (
    requestFn: () => Promise<DownloadableResponse>,
    fallbackFilename: string,
    options?: DownloadOptions
  ) => Promise<void>;
  /** True while a download is in flight. Drive a button's `disabled` from this. */
  isDownloading: boolean;
}

/**
 * The message the server sent with a failed download, or `undefined` when it sent none.
 *
 * Only meaningful on an error the hook has already handed to an `onError` callback: it is that step
 * that turns the `responseType:'blob'` error body into the JSON this reads. A body the server never
 * sent, one that is not JSON with a `message`, or one whose `message` is empty all yield
 * `undefined` -- so a caller rendering it inline still needs its own generic copy for that case,
 * and an empty `message` cannot leave it with an empty banner.
 */
export const getDownloadErrorMessage = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }
  const data: unknown = error.response?.data;
  if (typeof data !== 'object' || data === null) {
    return undefined;
  }
  const { message } = data as { message?: unknown };
  return typeof message === 'string' && message !== '' ? message : undefined;
};

/**
 * Download a file from an endpoint that returns binary, with in-flight state and readable errors.
 *
 * One instance is one download slot: while a download is running, further `download` calls are
 * ignored. Two independent download buttons on a screen should therefore call this hook twice
 * rather than share one instance.
 *
 * Callers own the request, the fallback filename and -- through `onError` -- how a failure is shown
 * to the user; all three are per-call, so one instance can serve several different downloads. What
 * this hook owns is the parts that were being reimplemented at every download site.
 */
export const useFileDownload = (): UseFileDownloadResult => {
  const [isDownloading, setIsDownloading] = useState(false);
  // The guard has to be a ref, not `isDownloading`: `download` is a `useCallback` with an empty
  // dependency list (see below), so its closure captures `isDownloading` as `false` for the life of
  // the component and never observes the flip. A ref is the only value that reads current from
  // inside a stable callback, which is what makes it -- and not the state -- the thing that stops
  // two `download()` calls landing in the same tick. Separate clicks are separate tasks and a
  // button's `disabled` already covers those; the state exists to drive that visible affordance.
  const isDownloadingRef = useRef(false);

  const download = useCallback(
    async (
      requestFn: () => Promise<DownloadableResponse>,
      fallbackFilename: string,
      { onError }: DownloadOptions = {}
    ) => {
      if (isDownloadingRef.current) {
        // Ignored rather than rejected: a caller that fires twice is not an error to report.
        return;
      }
      isDownloadingRef.current = true;
      setIsDownloading(true);

      try {
        const response = await requestFn();
        downloadResponseAsFile(response, { fallbackFilename });
      } catch (error) {
        // On a responseType:'blob' request axios hands back the error body as a Blob too, so nothing
        // downstream can read `response.data.message` off it: the reporter falls through to its
        // generic unexpected-error copy. Re-read it and put the parsed body back on the *same*
        // AxiosError instance -- reportAxiosError replaces a non-Error argument with
        // `new Error(String(error))`, so handing it a spread copy would drop the response entirely.
        // An `onError` caller gets the same treatment: reading the server's message off a Blob is
        // the awkward part every download site was getting wrong, and it is not the dialog.
        if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          try {
            error.response.data = JSON.parse(text);
          } catch {
            // A non-JSON body (an edge proxy's HTML page, say) carries no message to surface;
            // leaving the Blob in place keeps the reporter's status-keyed branches working.
          }
        }
        if (onError) {
          await onError(error);
        } else {
          await reportAxiosError(error);
        }
      } finally {
        // Released in a finally: clearing it only on success would leave the button disabled
        // forever after a single failed download.
        isDownloadingRef.current = false;
        setIsDownloading(false);
      }
    },
    // No dependencies: everything the callback needs is now either a parameter or a ref, so nothing
    // captured here can go stale. `download` is consequently referentially stable, though no
    // consumer depends on that today.
    []
  );

  return { download, isDownloading };
};
